import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Input, DatePicker, Select, Button, Table, Statistic, Divider, Space, Typography, Tag, message, Popconfirm, Form, Modal } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, EyeOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

import useAuthStore from '../../store/useAuthStore';
import usePermissions from '../../hooks/usePermissions';
import { MODULE_IDS, GST_RATES } from '../../utils/constants';
import { formatCurrency, formatQty, formatDate } from '../../utils/formatters';

import { getPurchases, getPurchaseById, createPurchase, updatePurchase, deletePurchase } from '../../api/rawPurchase.api';

import PageHeader from '../../components/common/PageHeader';
import SearchableSelect from '../../components/common/SearchableSelect';
import ConfirmDelete from '../../components/common/ConfirmDelete';
import DataTable from '../../components/common/DataTable';

const { Title, Text } = Typography;

// --- Sub-component for viewing expanded bill details ---
const ExpandedBillDetails = ({ plantId, rpsid }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['purchaseDetail', plantId, rpsid],
    queryFn: () => getPurchaseById(plantId, rpsid),
    enabled: !!rpsid && !!plantId,
  });

  const columns = [
    { title: 'Raw Material', dataIndex: 'rawName', key: 'rawName' },
    { title: 'HSN', dataIndex: 'hsnCode', key: 'hsnCode' },
    { title: 'Qty', dataIndex: 'qty', key: 'qty', render: val => formatQty(val) },
    { title: 'Unit', dataIndex: 'unitDisplayName', key: 'unitDisplayName' },
    { title: 'Rate', dataIndex: 'rate', key: 'rate', render: val => formatCurrency(val) },
    { title: 'Dis.', dataIndex: 'discount', key: 'discount', render: val => formatCurrency(val) },
    { title: 'Taxable', dataIndex: 'taxable', key: 'taxable', render: val => formatCurrency(val) },
    { title: 'GST Amt', dataIndex: 'gstAmt', key: 'gstAmt', render: val => formatCurrency(val) },
    { title: 'Total', dataIndex: 'amount', key: 'amount', render: val => formatCurrency(val) },
  ];

  return <Table columns={columns} dataSource={data?.details || []} pagination={false} size="small" loading={isLoading} rowKey="rpid" />;
};

// --- MAIN COMPONENT ---
const RawPurchasePage = () => {
  const { user } = useAuthStore();
  const plantId = user?.plantID;
  const queryClient = useQueryClient();
  const { canAdd, canEdit, canDelete } = usePermissions(MODULE_IDS.RAW_PURCHASE);

  // Forms
  const [headerForm] = Form.useForm();
  const [itemForm] = Form.useForm();

  // State
  const [lineItems, setLineItems] = useState([]);
  const [editingRpsid, setEditingRpsid] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedRaw, setSelectedRaw] = useState(null);
  
  // History Table State
  const [historyParams, setHistoryParams] = useState({ current: 1, pageSize: 10, from: null, to: null, vid: null });
  const [historyVendorId, setHistoryVendorId] = useState(null);

  // Watch Header GST to recalculate items if it changes
  const currentGstPercent = Form.useWatch('gstPercent', headerForm) ?? 18;

  useEffect(() => {
    // Recalculate line items if global GST percent changes
    if (lineItems.length > 0) {
      const updatedLines = lineItems.map(item => {
        const effectiveRate = item.rate - (item.discount || 0);
        const taxable = item.qty * effectiveRate;
        const gstAmt = taxable * (currentGstPercent / 100);
        return { ...item, gstPercent: currentGstPercent, taxable, gstAmt, amount: taxable + gstAmt };
      });
      setLineItems(updatedLines);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGstPercent]);

  // --- React Query: Fetch History ---
  const { data: historyData, isLoading: isHistoryLoading } = useQuery({
    queryKey: ['purchases', plantId, historyParams, historyVendorId],
    queryFn: () => getPurchases(plantId, {
      page: historyParams.current,
      pageSize: historyParams.pageSize,
      from: historyParams.from ? historyParams.from.format('YYYY-MM-DD') : null,
      to: historyParams.to ? historyParams.to.format('YYYY-MM-DD') : null,
      vid: historyVendorId
    }),
    enabled: !!plantId && !!historyVendorId,
  });

  // --- Mutations ---
  const createMutation = useMutation({
    mutationFn: (payload) => createPurchase(plantId, payload),
    onSuccess: () => {
      message.success('Purchase bill saved successfully!');
      handleResetForm();
      queryClient.invalidateQueries(['purchases']);
    },
    onError: (err) => message.error(err.message || 'Error saving bill')
  });

  const updateMutation = useMutation({
    mutationFn: (payload) => updatePurchase(plantId, editingRpsid, payload),
    onSuccess: () => {
      message.success('Purchase bill updated successfully!');
      handleResetForm();
      queryClient.invalidateQueries(['purchases']);
    },
    onError: (err) => message.error(err.message || 'Error updating bill')
  });

  const deleteMutation = useMutation({
    mutationFn: (rpsid) => deletePurchase(plantId, rpsid),
    onSuccess: () => {
      message.success('Purchase bill deleted successfully!');
      queryClient.invalidateQueries(['purchases']);
    },
    onError: (err) => message.error(err.message || 'Error deleting bill')
  });

  // --- Handlers ---
  const handleAddLineItem = async () => {
    try {
      const values = await itemForm.validateFields();
      if (!selectedRaw) return message.error("Please select a valid Raw Material");

      const qty = Number(values.qty);
      const rate = Number(values.rate);
      const discount = Number(values.discount || 0);

      const effectiveRate = rate - discount; // Discount rate se minus hoga
      const taxable = qty * effectiveRate;
      const gstAmt = taxable * (currentGstPercent / 100);
      const amount = taxable + gstAmt;

      const newItem = {
        id: Date.now(), // Temp local ID
        rawID: selectedRaw.rawID,
        rawName: selectedRaw.rawName,
        hsnCode: values.hsnCode,
        qty: qty,
        unitID: selectedRaw.unitID,
        unitDisplayName: selectedRaw.unitDisplayName,
        rate: rate,
        discount: discount,
        gstPercent: currentGstPercent,
        taxable: taxable,
        gstAmt: gstAmt,
        amount: amount
      };

      setLineItems([...lineItems, newItem]);
      
      // Reset Item Form
      itemForm.resetFields();
      setSelectedRaw(null);
    } catch (error) {
      console.log('Validation failed:', error);
    }
  };

  const handleRemoveItem = (id) => {
    setLineItems(lineItems.filter(item => item.id !== id));
  };

  const handleSaveBill = async () => {
    try {
      const headerValues = await headerForm.validateFields();
      if (lineItems.length === 0) return message.error("Please add at least one item to the bill");

      const totals = calculateTotals();

      const payload = {
        billNo: headerValues.billNo,
        billDate: headerValues.billDate.format('YYYY-MM-DD'),
        vid: headerValues.vid,
        gstPercent: currentGstPercent,
        taxable: totals.taxable,
        gstAmt: totals.gst,
        totalAmount: totals.grand,
        details: lineItems.map(item => ({
          rawID: item.rawID,
          qty: item.qty,
          unitID: item.unitID,
          rate: item.rate,
          discount: item.discount,
          gstPercent: item.gstPercent,
          hsnCode: item.hsnCode
        }))
      };

      if (editingRpsid) {
        updateMutation.mutate(payload);
      } else {
        createMutation.mutate(payload);
      }
    } catch (error) {
      message.error("Please fill all required header fields");
    }
  };

  const handleEditBill = async (record) => {
    try {
      const fullBill = await getPurchaseById(plantId, record.rpsid);
      setEditingRpsid(fullBill.rpsid);
      
      // Populate Header
      headerForm.setFieldsValue({
        vid: fullBill.vid,
        billNo: fullBill.billNo,
        billDate: dayjs(fullBill.billDate),
        gstPercent: fullBill.gstPercent
      });
      setSelectedVendor({ vendorName: fullBill.vendorName }); // Minimal info for display

      // Populate Items
      const mappedItems = fullBill.details.map((d, index) => ({
        id: d.rpid || index, // Use DB RPID as temp ID
        rawID: d.rawID,
        rawName: d.rawName,
        hsnCode: d.hsnCode,
        qty: d.qty,
        unitID: d.unitID,
        unitDisplayName: d.unitDisplayName,
        rate: d.rate,
        discount: d.discount,
        gstPercent: d.gstPercent,
        taxable: d.taxable,
        gstAmt: d.gstAmt,
        amount: d.amount
      }));
      setLineItems(mappedItems);
      
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      message.error("Failed to fetch bill details for editing");
    }
  };

  const handleResetForm = () => {
    headerForm.resetFields();
    itemForm.resetFields();
    setLineItems([]);
    setEditingRpsid(null);
    setSelectedVendor(null);
    setSelectedRaw(null);
    setHistoryVendorId(null);
  };

  const calculateTotals = () => {
    return lineItems.reduce((acc, curr) => ({
      taxable: acc.taxable + curr.taxable,
      gst: acc.gst + curr.gstAmt,
      grand: acc.grand + curr.amount
    }), { taxable: 0, gst: 0, grand: 0 });
  };

  const totals = calculateTotals();

  // --- Table Configurations ---
  const lineItemColumns = [
    { title: '#', render: (text, record, index) => index + 1, width: 50 },
    { title: 'Raw Material', dataIndex: 'rawName', key: 'rawName' },
    { title: 'HSN', dataIndex: 'hsnCode', key: 'hsnCode' },
    { title: 'Qty', dataIndex: 'qty', key: 'qty', render: val => formatQty(val) },
    { title: 'Unit', dataIndex: 'unitDisplayName', key: 'unitDisplayName' },
    { title: 'Rate', dataIndex: 'rate', key: 'rate', render: val => formatCurrency(val) },
    { title: 'Disc.', dataIndex: 'discount', key: 'discount', render: val => formatCurrency(val) },
    { title: 'Taxable', dataIndex: 'taxable', key: 'taxable', render: val => formatCurrency(val) },
    { title: 'GST', dataIndex: 'gstAmt', key: 'gstAmt', render: val => formatCurrency(val) },
    { title: 'Amount', dataIndex: 'amount', key: 'amount', render: val => formatCurrency(val) },
    { 
      title: 'Action', 
      key: 'action', 
      render: (_, record) => (
        <Button danger type="text" icon={<DeleteOutlined />} onClick={() => handleRemoveItem(record.id)} />
      ) 
    }
  ];

  const historyColumns = [
    { title: 'Bill No', dataIndex: 'billNo', key: 'billNo', fixed: 'left' },
    { title: 'Date', dataIndex: 'billDate', key: 'billDate', render: val => formatDate(val) },
    { title: 'Vendor', dataIndex: 'vendorName', key: 'vendorName' },
    { title: 'Taxable', dataIndex: 'taxable', key: 'taxable', render: val => formatCurrency(val) },
    { title: 'GST', dataIndex: 'gstAmt', key: 'gstAmt', render: val => formatCurrency(val) },
    { title: 'Total', dataIndex: 'totalAmount', key: 'totalAmount', render: val => <b>{formatCurrency(val)}</b> },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {canEdit && <Button type="text" icon={<EditOutlined style={{ color: '#1890ff' }}/>} onClick={() => handleEditBill(record)} />}
          {canDelete && (
            <ConfirmDelete 
              itemName={`Bill ${record.billNo}`} 
              onConfirm={() => deleteMutation.mutate(record.rpsid)} 
            />
          )}
        </Space>
      )
    }
  ];

  return (
    <div>
      <PageHeader 
        title="Raw Material Purchase" 
        subtitle="Record incoming stock and vendor bills" 
        canAdd={false} // No Add drawer needed here
      />

      {/* SECTION A: BILL HEADER */}
      <Card title={editingRpsid ? "Edit Purchase Entry" : "New Purchase Entry"} style={{ marginBottom: 16 }} 
        extra={editingRpsid && <Button onClick={handleResetForm}>Cancel Edit</Button>}>
        <Form form={headerForm} layout="vertical" initialValues={{ gstPercent: 18, billDate: dayjs() }}>
          <Row gutter={16}>
            <Col xs={24} md={12} lg={14}>
              <Form.Item label="Select Vendor" name="vid" rules={[{ required: true, message: 'Vendor required' }]}>
                <SearchableSelect
                  apiUrl={`/plants/${plantId}/vendors/search`}
                  valueField="vid"
                  labelField="vendorName"
                  placeholder="Search vendor by name or GSTIN..."
                  onSelect={v => {
                    headerForm.setFieldsValue({ vid: v ? v.vid : undefined });
                    setSelectedVendor(v);
                    if (v) {
                      setHistoryVendorId(v.vid);
                      setHistoryParams(prev => ({ ...prev, current: 1 }));
                    }
                  }}
                />
              </Form.Item>
              {selectedVendor && selectedVendor.gstin && (
                <div style={{ marginTop: -15, marginBottom: 15 }}>
                  <Tag color="blue">GSTIN: {selectedVendor.gstin}</Tag>
                  {selectedVendor.stateName && <Tag>{selectedVendor.stateName}</Tag>}
                </div>
              )}
            </Col>
            <Col xs={12} md={6} lg={5}>
              <Form.Item label="Bill No." name="billNo" rules={[{ required: true, message: 'Required' }]}>
                <Input placeholder="Enter Bill No" />
              </Form.Item>
            </Col>
            <Col xs={12} md={6} lg={5}>
              <Form.Item label="Bill Date" name="billDate" rules={[{ required: true, message: 'Required' }]}>
                <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={12} md={6} lg={5}>
              <Form.Item label="Global GST %" name="gstPercent">
                <Select options={GST_RATES} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>

      {/* SECTION B: ADD LINE ITEM */}
      <Card title="Add Raw Material" style={{ marginBottom: 16, background: '#fafafa' }} size="small">
        <Form form={itemForm} layout="vertical">
          <Row gutter={12} align="bottom">
            <Col xs={24} md={6}>
              <Form.Item label="Raw Material" name="rawID" rules={[{ required: true, message: 'Required' }]} style={{ marginBottom: 0 }}>
                <SearchableSelect
                  apiUrl={`/plants/${plantId}/raw-materials`}
                  valueField="rawID"
                  labelField="rawName"
                  placeholder="Search raw material"
                  onSelect={(v) => {
                    itemForm.setFieldsValue({ rawID: v ? v.rawID : undefined });
                    setSelectedRaw(v);
                    if (v) {
                      itemForm.setFieldsValue({
                        hsnCode: v.hsnCode || '',
                        unitDisplay: v.unitDisplayName || ''
                      });
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={3}>
              <Form.Item label="HSN Code" name="hsnCode" style={{ marginBottom: 0 }}>
                <Input placeholder="HSN" />
              </Form.Item>
            </Col>
            <Col xs={12} md={3}>
              <Form.Item label="Qty" name="qty" rules={[{ required: true, message: 'Required' }]} style={{ marginBottom: 0 }}>
                <Input type="number" step="any" min="0" placeholder="0.00" />
              </Form.Item>
            </Col>
            <Col xs={12} md={3}>
              <Form.Item label="Unit" name="unitDisplay" style={{ marginBottom: 0 }}>
                <Input disabled placeholder="Auto" />
              </Form.Item>
            </Col>
            <Col xs={12} md={3}>
              <Form.Item label="Rate (₹)" name="rate" rules={[{ required: true, message: 'Required' }]} style={{ marginBottom: 0 }}>
                <Input type="number" step="any" min="0" placeholder="0.00" />
              </Form.Item>
            </Col>
            <Col xs={12} md={3}>
              <Form.Item label="Discount (₹)" name="discount" style={{ marginBottom: 0 }}>
                <Input type="number" step="any" min="0" placeholder="0.00" />
              </Form.Item>
            </Col>
            <Col xs={12} md={3}>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAddLineItem} block style={{ marginBottom: 0 }}>
                Add
              </Button>
            </Col>
          </Row>
        </Form>
      </Card>

      {/* SECTION C: LINE ITEMS TABLE */}
      <Card bodyStyle={{ padding: 0 }} style={{ marginBottom: 16 }}>
        <Table 
          columns={lineItemColumns} 
          dataSource={lineItems} 
          pagination={false} 
          rowKey="id"
          locale={{ emptyText: "No items added yet. Search and add materials above." }}
          scroll={{ x: 'max-content' }}
        />
      </Card>

      {/* SECTION D: BILL SUMMARY */}
      <Card>
        <Row align="middle" justify="space-between">
          <Col xs={24} md={16}>
            <Row gutter={32}>
              <Col>
                <Statistic title="Total Taxable" value={totals.taxable} precision={2} prefix="₹" />
              </Col>
              <Col>
                <Statistic title="Total GST" value={totals.gst} precision={2} prefix="₹" />
              </Col>
              <Col>
                <Statistic title="Grand Total" value={totals.grand} precision={2} prefix="₹" styles={{ value: { color: '#3f8600', fontWeight: 'bold' } }} />
              </Col>
            </Row>
          </Col>
          <Col xs={24} md={8} style={{ textAlign: 'right', marginTop: '16px' }}>
            {canAdd && (
              <Button 
                type="primary" 
                size="large" 
                icon={<ShoppingCartOutlined />} 
                onClick={handleSaveBill}
                loading={createMutation.isPending || updateMutation.isPending}
                disabled={lineItems.length === 0}
              >
                {editingRpsid ? 'Update Purchase Bill' : 'Save Purchase Bill'}
              </Button>
            )}
          </Col>
        </Row>
      </Card>

      <Divider style={{ margin: '32px 0' }} orientation="left">Recent Purchase Bills</Divider>

      {/* SECTION E: PREVIOUS BILLS */}
      {historyVendorId && (
        <div style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <DatePicker.RangePicker 
                style={{ width: '100%' }}
                onChange={(dates) => {
                  setHistoryParams(prev => ({ 
                    ...prev, 
                    current: 1, 
                    from: dates ? dates[0] : null, 
                    to: dates ? dates[1] : null 
                  }));
                }} 
              />
            </Col>
          </Row>
        </div>
      )}

      {historyVendorId ? (
        <DataTable 
          columns={historyColumns} 
          data={historyData?.items || []} 
          loading={isHistoryLoading} 
          rowKey="rpsid"
          pagination={{
            current: historyParams.current,
            pageSize: historyParams.pageSize,
            total: historyData?.total || 0
          }}
          onChange={(pagination) => setHistoryParams(prev => ({ ...prev, current: pagination.current, pageSize: pagination.pageSize }))}
          expandable={{
            expandedRowRender: (record) => <ExpandedBillDetails plantId={plantId} rpsid={record.rpsid} />,
          }}
        />
      ) : (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            <ShoppingCartOutlined style={{ fontSize: 48, marginBottom: 16 }} />
            <div>Select a vendor above to view their purchase history</div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default RawPurchasePage;